import {
  COMPONENT_DRAFT_CONFIG,
  DEFAULT_VISUALIZATION_TYPE,
} from '@api/narrative-projects/utilities/constants';

import { UNTITLED_TOPIC_FALLBACK } from './constants';

const isDefined = (value) => value !== undefined && value !== null;

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!isDefined(value)) return [];
  return [value];
};

/**
 * Combines two value sets while removing duplicates and preserving falsy-but-valid entries.
 * @param {Array<*>} existingValues
 * @param {Array<*>} nextValues
 * @returns {Array<*>}
 */
export const mergeUniqueValues = (existingValues = [], nextValues = []) => {
  const merged = [...ensureArray(existingValues), ...ensureArray(nextValues)].filter(isDefined);
  return Array.from(new Set(merged));
};

/**
 * Builds the payload expected by createNarrative from transformed prompt answers and read-only values.
 * @param {Object<string, Array>} transformedAnswersMap
 * @param {Object} readOnlyPromptAnswers
 * @returns {Object}
 */
export const buildPromptAnswersPayload = (
  transformedAnswersMap = {},
  readOnlyPromptAnswers = {}
) => {
  const editableAnswers = Object.values(transformedAnswersMap || {})
    .filter(Boolean)
    .flat()
    .filter(Boolean)
    .reduce((acc, answer) => {
      if (!answer?.field) return acc;

      const { field, values } = answer;

      if (Array.isArray(values)) {
        acc[field] = mergeUniqueValues(acc[field], values);
      } else if (values?.date_range) {
        acc[field] = values.date_range;
      }

      return acc;
    }, {});

  const mergedAnswers = { ...(readOnlyPromptAnswers || {}) };

  Object.entries(editableAnswers).forEach(([field, values]) => {
    mergedAnswers[field] = Array.isArray(values)
      ? mergeUniqueValues(mergedAnswers[field], values)
      : values;
  });

  return mergedAnswers;
};

/**
 * Generates draft-topic shells for manually selected topics from the library.
 * @param {Array<{id: string|number, name?: string, vizType?: string}>} selectedTopicCards
 * @returns {Array<Object>}
 */
export const buildManualSelectedDrafts = (selectedTopicCards = []) =>
  selectedTopicCards
    .map((topic) => {
      if (!topic?.id) return null;
      const componentConfig = COMPONENT_DRAFT_CONFIG[topic.id] || {};
      const mockTitle = componentConfig.title;
      const resolvedTitle = mockTitle || topic.name || UNTITLED_TOPIC_FALLBACK;
      const resolvedType = topic.vizType || componentConfig.type || null;

      return {
        cleanTitle: resolvedTitle,
        componentId: topic.id,
        dataset: {
          type: resolvedType,
        },
        id: `selected-topic-${topic.id}`,
        title: resolvedTitle,
        visualizationType: resolvedType,
      };
    })
    .filter(Boolean);

/**
 * Merges draft topics derived from flow state with the manually selected drafts.
 * @param {Array<Object>} flowStateDraftTopics
 * @param {Array<Object>} manualSelectedDrafts
 * @returns {Array<Object>}
 */
export const mergeDraftTopics = (flowStateDraftTopics = [], manualSelectedDrafts = []) => {
  const dedupedFlowDrafts = [];
  const existingIds = new Set();
  const manualDraftsByComponentId = new Map(
    manualSelectedDrafts
      .filter((draft) => isDefined(draft?.componentId))
      .map((draft) => [draft.componentId, draft])
  );

  flowStateDraftTopics.forEach((draft) => {
    if (!draft) return;
    const componentId = draft.componentId;
    if (isDefined(componentId)) {
      if (existingIds.has(componentId)) return;
      existingIds.add(componentId);

      const componentConfig = COMPONENT_DRAFT_CONFIG[componentId] || {};
      const manualDraft = manualDraftsByComponentId.get(componentId);
      const manualType = manualDraft?.dataset?.type || manualDraft?.visualizationType;
      const shouldUseManualType =
        manualType && !componentConfig.type && draft.dataset?.type === DEFAULT_VISUALIZATION_TYPE;

      if (shouldUseManualType) {
        dedupedFlowDrafts.push({
          ...draft,
          cleanTitle: draft.cleanTitle || manualDraft.cleanTitle,
          dataset: {
            ...(draft.dataset || {}),
            type: manualType,
          },
          title: draft.title || manualDraft.title,
          visualizationType: manualType,
        });
        return;
      }
    }
    dedupedFlowDrafts.push(draft);
  });

  if (!manualSelectedDrafts.length) return dedupedFlowDrafts;

  const filteredManualDrafts = manualSelectedDrafts.filter((draft) => {
    if (!draft) return false;
    const { componentId } = draft;
    if (!isDefined(componentId) || existingIds.has(componentId)) return false;
    existingIds.add(componentId);
    return true;
  });

  return [...dedupedFlowDrafts, ...filteredManualDrafts];
};

/**
 * Extracts unique component IDs from a list of draft topics.
 * @param {Array<Object>} draftTopics
 * @returns {Array<string|number>}
 */
export const extractComponentIds = (draftTopics = []) =>
  Array.from(
    new Set(
      draftTopics
        .map((draft) => draft?.componentId)
        .filter((componentId) => componentId !== undefined && componentId !== null)
    )
  );

/**
 * Enriches draft topic shells with API data returned from readDraftTopics.
 * @param {Array<Object>} draftTopics
 * @param {Object<string|number, Object>} draftDatasetMap
 * @returns {Array<Object>}
 */
export const buildEnrichedDraftTopics = (draftTopics = [], draftDatasetMap = {}) => {
  if (!draftTopics.length) return [];

  return draftTopics.map((draft) => {
    const apiDraft = draftDatasetMap?.[draft.componentId];

    if (!apiDraft?.dataset) return draft;

    return {
      ...draft,
      cleanTitle: apiDraft.cleanTitle || draft.cleanTitle,
      dataset: {
        ...apiDraft.dataset,
        isDraftDisplayData: true,
        type: apiDraft.dataset.type || draft.dataset?.type,
      },
      sampleSize: apiDraft.sampleSize ?? draft.sampleSize,
      title: apiDraft.cleanTitle || apiDraft.title || draft.title,
    };
  });
};

/**
 * Removes dataset-map entries that are no longer referenced by the provided component IDs.
 * @param {Object<string|number, Object>} draftDatasetMap
 * @param {Array<string|number>} componentIds
 * @returns {Object<string|number, Object>}
 */
export const pruneDraftDatasetMap = (draftDatasetMap = {}, componentIds = []) => {
  if (!componentIds.length) {
    return Object.keys(draftDatasetMap).length ? {} : draftDatasetMap;
  }

  const nextEntries = componentIds
    .filter((id) => draftDatasetMap[id])
    .map((id) => [id, draftDatasetMap[id]]);

  if (nextEntries.length === Object.keys(draftDatasetMap).length) {
    return draftDatasetMap;
  }

  return Object.fromEntries(nextEntries);
};
